import React from "react";
import styled from "styled-components";
import { BackNavRow, PageContainer } from "../common/CommonStyles";
import { useTranslation, Trans } from "react-i18next";
import { Link } from "react-router-dom";
import { Row, Col } from "react-bootstrap";
import { COLOR_TEXT_PRIMARY } from "../common/colors";

const MainVideo = styled.video`
  width: min(100%, calc(40vh * 16 / 9));
  min-height: 200px;
  aspect-ratio: 16 / 9;
  object-fit: cover;
  cursor: pointer;
  display: block;
`;

const List = styled.ul`
  list-style: none;
  margin-left: auto;
  margin-right: auto;
  max-width: 640px;
`;

const ListItem = styled.li`
  &::before {
    content: "✓ ";
    margin-right: 4px;
  }
  text-indent: -1.4em;
`;

interface Props {
  back?: () => void;
}

export const Paywall: React.FC<Props> = ({ back }) => {
  const { t } = useTranslation();

  const videoRef = React.useRef<HTMLVideoElement | null>(null);

  const playVideo = (e) => {
    const video = videoRef.current;
    if (!video) return;
    e.preventDefault();
    e.stopPropagation(); // We play the video ourself - avoid weird interaction on mobile where the click propagates to pause the video
    video.play();
    video.setAttribute("controls", "controls");
  };

  return (
    <PageContainer>
      <BackNavRow callback={back} label={t("generic_nav_back")}>
        <h3 className="my-4">{t("paywall_log_in_to_continue")}</h3>
      </BackNavRow>
      <div className="justify-content-center mb-5">
        <div className="d-flex flex-column py-2 gap-2 align-items-center text-center">
          <Row>
            <Col>
              <MainVideo
                className="mx-auto"
                ref={videoRef}
                poster={`${process.env.PUBLIC_URL}/super_teacher_for_parents_thumbnail.jpg`}
                onClick={playVideo}
              >
                <source
                  src={`${process.env.PUBLIC_URL}/super_teacher_for_parents.mp4`}
                  type="video/mp4"
                />
                Video could not be loaded.
              </MainVideo>

              <List className="my-4 text-start">
                <ListItem>
                  <Trans t={t} i18nKey="paywall_unlimited_tutoring">
                    <strong>Unlimited private tutoring</strong> for every
                    student
                  </Trans>
                </ListItem>
                <ListItem>
                  <Trans t={t} i18nKey="paywall_differentiated_instruction">
                    <strong>Fully differentiated instruction</strong> for
                    classrooms
                  </Trans>
                </ListItem>
                <ListItem>
                  <Trans t={t} i18nKey="paywall_tools_for_teachers">
                    <strong>Dashboards and assessments</strong> for teachers
                  </Trans>
                </ListItem>
                <ListItem>
                  <Trans t={t} i18nKey="paywall_full_courses">
                    <strong>Math, reading, and more</strong> full courses for
                    Pre-K to 5th grade
                  </Trans>
                </ListItem>
                <ListItem>
                  <Trans t={t} i18nKey="paywall_low_cost">
                    <strong>A fraction of the cost</strong> of hiring and
                    scheduling a private tutor
                  </Trans>
                </ListItem>
              </List>
              <p style={{ fontSize: "1.25em" }}>
                <Trans
                  i18nKey="paywall_contact"
                  values={{ email: "support@getsuperteacher.com" }}
                  components={[<strong />]}
                />
              </p>
              <hr className="mx-auto " style={{ maxWidth: 600, margin: 30 }} />
              <p style={{ color: COLOR_TEXT_PRIMARY }}>
                {t("paywall_school_uses_app")}{" "}
                <Link className="text-decoration-none" to="/account/login/">
                  {t("paywall_log_in")}
                </Link>
              </p>
              <p>
                {
                  <Trans
                    t={t}
                    i18nKey="paywall_create_non_school_account"
                    components={{
                      linkText: (
                        <Link
                          className="text-decoration-none"
                          to="/account/upgrade/"
                        />
                      ),
                    }}
                  />
                }
              </p>
            </Col>
          </Row>
        </div>
      </div>
    </PageContainer>
  );
};
